import React, { useEffect } from 'react';

import { QuestionCircleOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import classnames from 'classnames';
import OText from 'components/CText/OText';
import { FlexContainer } from 'components/Containers/FlexContainer/FlexContainer';
import { Row as CRow } from 'components/Containers/Row/Row';
import DifferenceTable from 'components/DifferencesTable';
import DifferentialText from 'components/DifferentialText';
import { Image } from 'components/Image/Image';
import { Button } from 'components/form/Button/Button';
import Label from 'components/form/Label/Label';
import { formatAsFull, monthsToYears } from 'helpers/dateHelper';

import { get } from 'helpers/objectHelpers';

import './WhatsChangedReport.scss';
import {
  CREDIT_AGE,
  CREDIT_INQUIRIES,
} from 'pages_factor/constants/Factor.constants';

function RefreshTrigger(props) {
  const { expUpdateScore } = props;

  if (!props.showRefreshIcon) return '';
  return (
    <div
      onClick={props.onRefreshClick}
      style={{
        position: 'absolute',
        top: 12,
        right: 25,
      }}
    >
      {expUpdateScore ? (
        <>
          <Label
            type="Subtitle-2"
            color="secondary_text_light"
            className="link refresh-label"
          >
            Refresh
          </Label>
          <div className="progress__refreshContainer__icon updateScore">
            <Image imgClass={'refreshBlue'} />
          </div>
        </>
      ) : (
        <div className="secondary_text_light pointer">
          <OText
            text={
              props?.optmizelyCreditFactorScore?.config?.credit_factor_score
                ?.text
            }
          />
          <span className="margl5">
            <Image imgClass={'arrowRight'} />
          </span>
        </div>
      )}
    </div>
  );
}

export const TotalCreditLimitDecreaseTooltip = () => {
  return (
    <FlexContainer>
      <Tooltip
        title={
          " If you're seeing an unexpected decrease in your total credit limit, this may be due to the fact that we're no longer including cards without preset limits when we calculate that amount."
        }
        placement="bottomLeft"
      >
        <span>{'Total Credit Limit'} </span>
        <QuestionCircleOutlined
          className="fontGray300"
          style={{ display: 'inline-block', position: 'relative', top: -3 }}
        />
      </Tooltip>
    </FlexContainer>
  );
};

const WhatsChangedReport = props => {
  return (
    <div
      className="whats-changed"
      data-testid={`${props.datatestid}-whats-changed`}
    >
      <CRow className="pos-rel">
        <Label className="mobileHide" type="H3" typeHeader={'h2'}>
          What's changed?
        </Label>
        <Label className="desktopHide" type="H5" typeHeader={'h2'}>
          What's changed?
        </Label>
        <RefreshTrigger {...props} />
      </CRow>
      <WhatsChangedBody {...props} />
    </div>
  );
};

function Report(props) {
  const { Row, Cell, Header, HeaderCell, CellHeader, CellData, Body } =
    DifferenceTable;
  const previousAge =
    props.creditAge.previous !== '--'
      ? monthsToYears(props.creditAge.previous)
      : '--';
  const currentAge =
    props.creditAge.current !== '--'
      ? monthsToYears(props.creditAge.current)
      : '--';
  //eslint-disable-next-line
  useEffect(() => props.sawThisCreditReport && props.sawThisCreditReport(), []);
  const creditReportGrade = props.notApplicable
    ? 'N/A'
    : `${props.creditUsage.current}%`;
  return (
    <CRow>
      <DifferenceTable datatestid="whats-changed-table">
        <Header>
          <HeaderCell>
            Previous report
            {+!!props.timestampForLastFullRefresh && (
              <div
                data-testid="wc-previous-report-date"
                className="text-12 fontGray300 normal"
              >
                as of {formatAsFull(props.timestampForLastFullRefresh)}
              </div>
            )}
          </HeaderCell>
          <HeaderCell>
            Current report
            <div
              data-testid="wc-current-report-date"
              className="text-12 fontGray300 normal"
            >
              as of {formatAsFull(get(props.creditInfo, ['reportPulledDate']))}
            </div>
          </HeaderCell>
        </Header>
        <Body>
          <Row>
            <Cell>
              <CellData
                datatestid="wc-creditscore-previous"
                fontClass="text-44 bold"
              >
                <span className="padR8 inblock">
                  {props.creditScore.previous}
                </span>
              </CellData>
            </Cell>
            <Cell>
              <CellData
                datatestid="wc-creditscore-current"
                fontClass="text-44 bold"
              >
                <span className="value">{props.creditScore.current}</span>
                <DifferentialText
                  datatestid="creditscore-table-diff"
                  {...props.creditScore}
                  fontSizeClass="text-12"
                />
              </CellData>
            </Cell>
          </Row>

          <Row>
            <Cell>
              <CellHeader>Negative Marks</CellHeader>
              <CellData datatestid="wc-negativemarks-previous">
                <span className="padR8 inblock">
                  {props.negativeMarks.previous}
                </span>
              </CellData>
            </Cell>
            <Cell>
              <CellHeader>Negative Marks</CellHeader>
              <CellData datatestid="wc-negativemarks-current">
                <span className="padR8 inblock bold">
                  {props.negativeMarks.current}
                </span>
                <DifferentialText
                  datatestid="negativemarks-table-diff"
                  {...props.negativeMarks}
                  fontSizeClass="text-12"
                />
              </CellData>
            </Cell>
          </Row>

          <Row>
            <Cell>
              <CellHeader>Credit Usage</CellHeader>
              <CellData datatestid="wc-creditusage-previous">
                <span className="padR8 inblock">
                  {props.creditUsage.previous}%
                </span>
              </CellData>
            </Cell>
            <Cell>
              <CellHeader>Credit Usage</CellHeader>
              <CellData datatestid="wc-creditusage-current">
                <span className="padR8 inblock bold">{creditReportGrade}</span>
                <DifferentialText
                  datatestid="creditusage-table-diff"
                  {...props.creditUsage}
                  fontSizeClass="text-12"
                />
              </CellData>
            </Cell>
          </Row>

          <Row>
            <Cell>
              <CellHeader>Total Credit Limit</CellHeader>
              <CellData datatestid="wc-creditlimit-previous">
                ${props.creditLimit.previous.toLocaleString()}
              </CellData>
            </Cell>
            <Cell>
              <CellHeader>
                {props.creditLimit.difference >= 0 ? (
                  'Total Credit Limit'
                ) : (
                  <TotalCreditLimitDecreaseTooltip />
                )}
              </CellHeader>
              <CellData datatestid="wc-creditlimit-current">
                <span className="padR8 inblock bold">
                  ${props.creditLimit.current.toLocaleString()}
                </span>
                <DifferentialText
                  datatestid="creditlimit-table-diff"
                  {...props.creditLimit}
                  fontSizeClass="text-12"
                />
              </CellData>
            </Cell>
          </Row>

          <Row>
            <Cell>
              <CellHeader>{CREDIT_AGE}</CellHeader>
              <CellData datatestid="wc-creditage-previous">
                <span className="padR8 inblock">{previousAge}</span>
              </CellData>
            </Cell>
            <Cell>
              <CellHeader>{CREDIT_AGE}</CellHeader>
              <CellData datatestid="wc-creditage-current">
                <span className="padR8 inblock bold">{currentAge}</span>
                <DifferentialText
                  datatestid="creditage-table-diff"
                  {...props.creditAge}
                  fontSizeClass="text-12"
                />
              </CellData>
            </Cell>
          </Row>

          <Row>
            <Cell>
              <CellHeader>{CREDIT_INQUIRIES}</CellHeader>
              <CellData datatestid="wc-creditinquiries-previous">
                <span className="padR8 inblock">
                  {props.creditInquiries.previous}
                </span>
              </CellData>
            </Cell>
            <Cell>
              <CellHeader>{CREDIT_INQUIRIES}</CellHeader>
              <CellData datatestid="wc-creditinquiries-current">
                <span className="padR8 inblock bold">
                  {props.creditInquiries.current}
                </span>
                <DifferentialText
                  datatestid="creditinquiries-table-diff"
                  {...props.creditInquiries}
                  fontSizeClass="text-12"
                />
              </CellData>
            </Cell>
          </Row>
        </Body>
      </DifferenceTable>
    </CRow>
  );
}

function Prompt(props) {
  return (
    <>
      <CRow className={classnames('flex-container', 'flex-row-nowrap')}>
        <div className="inblock">
          <Image
            imgClass={`${props.config.quarterlyReport.images.entrance.whatIsChanged} bgImageWH94`}
          />
        </div>
        <div className="inblock padlr24">
          {props.config.quarterlyReport.scoreFactor.whatIsChangedText}
        </div>
      </CRow>
      <CRow className="flex-container justify-end flex-row padB5">
        <Button className="linkbutton" onClick={props.toggleReport}>
          View Current Report
        </Button>
      </CRow>
    </>
  );
}

function WhatsChangedBody(props) {
  if (props.isShowReport) {
    return <Report {...props} />;
  } else {
    return <Prompt {...props} />;
  }
}

export default WhatsChangedReport;
